import {
  type BaseQueryFn,
  skipToken,
  type TypedUseMutation,
  type TypedUseQuery,
} from '@reduxjs/toolkit/query/react';
import React, { useMemo } from 'react';

import { type UseCreateAndPollJobProps } from './create-job-polling-hook.types';

interface CreateJobPollingHookProps<
  CreateJobResultType,
  CreateJobQueryArg,
  PollJobResultType,
  PollJobQueryArg,
  BaseQuery extends BaseQueryFn,
> {
  useCreateJobMutation: TypedUseMutation<
    CreateJobResultType,
    CreateJobQueryArg,
    BaseQuery
  >;
  usePollJobQuery: TypedUseQuery<PollJobResultType, PollJobQueryArg, BaseQuery>;
  getPollJobQueryFromCreateJobResult: (
    createJobResult: CreateJobResultType,
  ) => PollJobQueryArg;
  pollingIntervalSeconds: number;
  pollingTimeoutSeconds: number;
  getPollJobIsSuccess: (pollJobResult: PollJobResultType) => boolean;
  getPollJobIsError: (pollJobResult: PollJobResultType) => boolean;
  getAreSameJobs: (
    createJobResult: CreateJobResultType,
    pollJobResult: PollJobResultType,
  ) => boolean;
}

export const createJobPollingHook = <
  CreateJobResultType,
  CreateJobQueryArg,
  PollJobResultType,
  PollJobQueryArg,
  BaseQuery extends BaseQueryFn,
>({
  useCreateJobMutation,
  usePollJobQuery,
  getPollJobQueryFromCreateJobResult,
  pollingIntervalSeconds,
  pollingTimeoutSeconds,
  getPollJobIsSuccess,
  getPollJobIsError,
  getAreSameJobs,
}: CreateJobPollingHookProps<
  CreateJobResultType,
  CreateJobQueryArg,
  PollJobResultType,
  PollJobQueryArg,
  BaseQuery
>) => {
  type UseCreateJobMutationTriggerArgs = Parameters<
    ReturnType<typeof useCreateJobMutation>[0]
  >;
  type UseCreateJobMutationQueryArg = UseCreateJobMutationTriggerArgs[0];
  type UsePollJobQueryArg = Parameters<typeof usePollJobQuery>[0];

  const useCreateAndPollJob = ({
    onJobStarted,
    onJobSuccess,
    onJobError,
    onJobTimeout,
  }: UseCreateAndPollJobProps<
    UseCreateJobMutationQueryArg,
    CreateJobResultType,
    PollJobResultType
  >) => {
    const jobStartTime = React.useRef<DOMHighResTimeStamp>(0);
    const [jobCanPoll, setJobCanPoll] = React.useState(true);

    const [
      createJobMutationTrigger,
      {
        data: createJobResult,
        isError: createJobIsError,
        isLoading: createJobIsLoading,
        isSuccess: createJobIsSuccess,
        originalArgs: createJobOriginalArgs,
      },
    ] = useCreateJobMutation();

    const pollJobQueryArg = React.useMemo(
      () =>
        createJobResult !== undefined &&
        createJobIsSuccess &&
        !createJobIsLoading
          ? getPollJobQueryFromCreateJobResult(createJobResult)
          : skipToken,
      [createJobResult, createJobIsSuccess, createJobIsLoading],
    );

    const {
      currentData: pollJobResult,
      isError: pollJobIsError,
      isSuccess: pollJobIsSuccess,
      isLoading: pollJobIsLoading,
    } = usePollJobQuery(pollJobQueryArg as UsePollJobQueryArg, {
      pollingInterval: jobCanPoll ? pollingIntervalSeconds * 1000 : undefined,
      refetchOnMountOrArgChange: true,
    });

    const hasCreateJobResult = createJobResult !== undefined;
    const hasCreateJobOriginalArgs = createJobOriginalArgs !== undefined;
    const hasPollJobResult = pollJobResult !== undefined;

    const areSameJobs =
      hasCreateJobResult &&
      hasPollJobResult &&
      getAreSameJobs(createJobResult, pollJobResult);
    const jobIsError =
      createJobIsError ||
      pollJobIsError ||
      (areSameJobs && hasPollJobResult && getPollJobIsError(pollJobResult));
    const jobIsSuccess =
      pollJobIsSuccess &&
      createJobIsSuccess &&
      hasPollJobResult &&
      areSameJobs &&
      getPollJobIsSuccess(pollJobResult);
    const jobIsRunning =
      createJobIsLoading ||
      pollJobIsLoading ||
      (areSameJobs &&
        hasPollJobResult &&
        jobCanPoll &&
        !jobIsSuccess &&
        !jobIsError);

    // job completion checks
    const canCompleteJobSuccess =
      jobIsSuccess &&
      hasCreateJobOriginalArgs &&
      hasCreateJobResult &&
      hasPollJobResult;
    const canCompleteJobError = jobIsError && hasCreateJobOriginalArgs;

    // timeout check
    const jobDurationMs = performance.now() - jobStartTime.current;
    const jobIsTimeout = jobDurationMs >= pollingTimeoutSeconds * 1000;

    React.useEffect(() => {
      // job is cancelled or has not yet been started
      if (!jobCanPoll) return;

      // job timed out
      if (jobIsTimeout && hasCreateJobOriginalArgs) {
        onJobTimeout?.({
          createJobOriginalArgs,
          durationSeconds: jobDurationMs / 1000,
        });
        setJobCanPoll(false);
        return;
      }

      // job is running
      if (!jobIsSuccess && !jobIsError) return;

      // job is successful
      if (canCompleteJobSuccess) {
        onJobSuccess?.({
          createJobOriginalArgs,
          createJobResult,
          pollJobResult,
          durationSeconds: jobDurationMs / 1000,
        });
      }

      // job is error
      if (canCompleteJobError) {
        onJobError?.({
          createJobOriginalArgs,
          pollJobResult,
          durationSeconds: jobDurationMs / 1000,
        });
      }

      setJobCanPoll(false);
    }, [
      hasCreateJobOriginalArgs,
      canCompleteJobSuccess,
      canCompleteJobError,
      createJobOriginalArgs,
      createJobResult,
      pollJobResult,
      jobCanPoll,
      jobIsError,
      jobIsSuccess,
      jobIsTimeout,
      jobDurationMs,
      onJobTimeout,
      onJobSuccess,
      onJobError,
    ]);

    const createJob = React.useCallback(
      async (createJobQueryArg: UseCreateJobMutationQueryArg) => {
        if (jobIsRunning) return;
        const mutationResult =
          await createJobMutationTrigger(createJobQueryArg);
        if (mutationResult.data) {
          jobStartTime.current = performance.now();
          setJobCanPoll(true);
          onJobStarted?.({
            createJobOriginalArgs: createJobQueryArg,
            createJobResult: mutationResult.data,
          });
        }
        return mutationResult;
      },
      [createJobMutationTrigger, onJobStarted, jobIsRunning],
    );

    const cancelJob = React.useCallback(() => {
      setJobCanPoll(false);
    }, []);

    return useMemo(
      () => ({
        createJob,
        cancelJob,
        jobIsRunning,
        jobIsSuccess,
        jobIsError,
        jobIsTimeout,
        pollJobResult,
      }),
      [
        createJob,
        cancelJob,
        jobIsRunning,
        jobIsSuccess,
        jobIsError,
        jobIsTimeout,
        pollJobResult,
      ],
    );
  };

  return useCreateAndPollJob;
};
